<template>
  <component
    :is="embedded ? 'div' : 'b-card'"
    class="mb-0"
  >
    <b-link
      v-if="!embedded"
      class="brand-logo"
    >
      <ok-logo />
    </b-link>

    <error-alert
      v-if="error"
      :error="error"
    />

    <div
      v-if="loading"
      class="text-center py-2"
    >
      <b-spinner class="mb-1" />
      <div>{{ $t('auth.mfa-loading-qr') }}</div>
    </div>

    <template v-else-if="step === 'scan'">
      <b-card-title class="mb-1">
        {{ $t('auth.mfa-setup-title') }}
      </b-card-title>
      <b-card-text class="mb-1">
        {{ $t('auth.mfa-setup-hint') }}
      </b-card-text>

      <div class="text-center mb-1">
        <div
          v-if="safeQr"
          class="mfa-qr"
          v-html="safeQr"
        />
        <p
          v-else-if="otpauthUri"
          dir="ltr"
          class="text-left small mb-0"
        >
          {{ otpauthUri }}
        </p>
      </div>

      <div
        v-if="manualSecret"
        class="mb-1"
      >
        <small class="d-block mb-50">{{ $t('auth.mfa-manual-key') }}</small>
        <b-form-input
          :value="manualSecret"
          readonly
          dir="ltr"
          class="text-left"
          @focus="$event.target.select()"
        />
      </div>

      <validation-observer
        ref="form"
        #default="{invalid}"
      >
        <b-form @submit.prevent="confirm">
          <mfa-code-field
            v-model="code"
            :disabled="submitting"
          />
          <b-button
            variant="primary"
            type="submit"
            block
            :disabled="invalid || submitting"
          >
            {{ $t('auth.mfa-verify') }}
            <b-spinner
              v-if="submitting"
              small
              class="ml-50"
            />
          </b-button>
        </b-form>
      </validation-observer>
    </template>

    <template v-else-if="step === 'codes'">
      <b-card-title class="mb-1">
        {{ $t('auth.mfa-recovery-title') }}
      </b-card-title>
      <mfa-recovery-codes
        :codes="recoveryCodes"
        :continuing="continuing"
        @continue="finish"
      />
    </template>

    <b-button
      v-if="!embedded && step !== 'codes'"
      variant="flat-secondary"
      block
      class="mt-1"
      :disabled="submitting || loading"
      @click="$emit('cancel')"
    >
      {{ $t('auth.back-login') }}
    </b-button>
  </component>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import {
  BButton, BCard, BCardText, BCardTitle, BForm, BFormInput, BLink, BSpinner,
} from 'bootstrap-vue'
import OkLogo from '@/@okmenu/icons/OkLogo'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import MfaAPI from '@/API/mfa-api'
import { isInvalidMfaCodeError, normalizeMfaCode } from '@/utils/mfa-code'
import MfaCodeField from '@/views/auth/mfa/MfaCodeField'
import MfaRecoveryCodes from '@/views/auth/mfa/MfaRecoveryCodes'

export default {
  name: 'MfaSetupForm',
  components: {
    MfaRecoveryCodes,
    MfaCodeField,
    ErrorAlert,
    OkLogo,
    ValidationObserver,
    BButton,
    BCard,
    BCardText,
    BCardTitle,
    BForm,
    BFormInput,
    BLink,
    BSpinner,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    authenticated: {
      type: Boolean,
      default: false,
    },
    continuing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      error: null,
      step: 'scan',
      code: '',
      qrSvg: '',
      otpauthUri: '',
      recoveryCodes: [],
      payload: null,
    }
  },
  computed: {
    mfaToken() {
      return this.authenticated ? '' : this.$store.getters['auth/mfaToken']
    },
    safeQr() {
      const svg = String(this.qrSvg || '').trim()
      if (!svg.includes('<svg')) return ''
      return svg
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    },
    manualSecret() {
      if (!this.otpauthUri) return ''
      try {
        return new URL(this.otpauthUri).searchParams.get('secret') || ''
      } catch (error) {
        return ''
      }
    },
  },
  mounted() {
    this.loadQr()
  },
  methods: {
    async loadQr() {
      if (!this.authenticated && !this.mfaToken) {
        this.error = this.$t('auth.mfa-token-missing')
        return
      }
      this.loading = true
      this.error = null
      try {
        const data = await MfaAPI.setup(this.authenticated ? undefined : this.mfaToken)
        this.qrSvg = data?.qr_svg || ''
        this.otpauthUri = data?.otpauth_uri || ''
        this.step = 'scan'
      } catch (error) {
        this.error = error
      } finally {
        this.loading = false
      }
    },
    async confirm() {
      this.error = null
      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      const code = normalizeMfaCode(this.code)
      if (!code) {
        this.error = this.$t('auth.mfa-code-format')
        return
      }

      this.submitting = true
      try {
        const payload = await MfaAPI.confirm(code, this.authenticated ? undefined : this.mfaToken)
        const codes = payload?.recovery_codes
        if (!Array.isArray(codes) || !codes.length) {
          this.error = this.$t('auth.mfa-codes-missing')
          return
        }
        this.payload = payload
        this.recoveryCodes = codes
        this.step = 'codes'
        this.$emit('enrolled', payload)
      } catch (error) {
        this.error = isInvalidMfaCodeError(error) ? this.$t('auth.mfa-invalid-code') : error
        this.code = ''
        this.$nextTick(() => this.$refs.form?.reset())
      } finally {
        this.submitting = false
      }
    },
    finish() {
      this.$emit('completed', this.payload)
    },
  },
}
</script>

<style lang="scss" scoped>
.mfa-qr {
  display: inline-block;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 12px;
  line-height: 0;
}

.mfa-qr ::v-deep svg {
  width: 180px;
  height: 180px;
}
</style>
